import { computed, Injectable, Signal, signal, WritableSignal } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpService } from '../helper/http.service';
import { getDatabase, ref } from 'firebase/database';
import { object } from 'rxfire/database';
import { QueryChange } from '@dashboard_models/rtdb';
import { AW } from '@airwallet/types';
import { FESorted } from '@services/search/search-service';

@Injectable({
    providedIn: 'root'
})
export class LocationService {
    private database = getDatabase();
    private orderUpdateTimeout: NodeJS.Timeout | null = null;

    locationNames: WritableSignal<AW.Misc.IdName[] | null> = signal(null);
    locationNameMap: Signal<Record<string, string>> = computed(() => {
        const locations: AW.Misc.IdName[] = this.locationNames() ?? [];
        return locations.reduce(
            (map, location) => {
                map[location.id] = location.name;
                return map;
            },
            {} as Record<string, string>
        );
    });
    constructor(private httpService: HttpService) {}

    /***********

            FIREBASE DATABASE CALLS

    **********/

    readDefaultBookingSettings(): Observable<QueryChange> {
        const dbRef = ref(this.database, `default_booking_settings`);
        return object(dbRef);
    }

    /***********

    HTTP SERVICE CALLS

    **********/
    async isLocationCustomIdTaken(val: string): Promise<boolean> {
        // Abusing the search exact function - as it responses very fast and only return on exect hit on custom location id
        return this.httpService._http<{ result: FESorted }>('search', `search/exact/custom_location_id/${encodeURIComponent(val)}`, 'get', { params: { full: 'false' } }).then(res => {
            console.log(res.result.location);
            return res.result.location.length > 0;
        });
    }
    async getLocation(onBehalf: string, locationId: string): Promise<AW.Location> {
        return this.httpService._http('fetch', `locations/${locationId}`, 'get', { onBehalf });
    }

    async getAllLocationNames(uid: string): Promise<AW.Misc.IdName[]> {
        return this.httpService.dynamicHttp(`api_location/names`, 'get', { onBehalf: uid }).then(locations => {
            const typedLocations = locations as AW.Misc.IdName[];
            this.locationNames.set(typedLocations);
            return typedLocations;
        });
    }
    async getAllLocationNamesMappedToUid(uid: string, mapToUid?: boolean): Promise<AW.Location.LocationNamesForUid[]> {
        return this.httpService.dynamicHttp(`api_location/names`, 'get', { onBehalf: uid, params: { mapToUid } });
    }

    async getAllLocationNamesRecord(uid: string): Promise<Record<string, string>> {
        return this.httpService.dynamicHttp(`api_location/names`, 'get', { onBehalf: uid });
    }

    validateAddress(address: AW.Misc.Address): Promise<any> {
        return this.httpService.dynamicHttp(`api_location/validate`, 'post', { body: { address } });
    }

    async sortDevices(params: AW.Misc.OrderDevicesParams, onBehalf: string): Promise<{ terminals: Record<string, AW.Terminal.Unit>; devices: Record<string, AW.Device.Unit> }> {
        return this.httpService.dynamicHttp(`api_device/sort_devices`, 'put', { body: params, onBehalf });
    }

    async resetSortDevices(locationId: string, onBehalf: string): Promise<{ terminals: Record<string, AW.Terminal.Unit>; devices: Record<string, AW.Device.Unit> }> {
        return this.httpService.dynamicHttp(`api_device/reset_sorted_devices`, 'put', { body: { locationId }, onBehalf });
    }

    async getLocations(uid: string, params: string): Promise<AW.SearchAndTable.LocationResponse> {
        return this.httpService.dynamicHttp(`api_location`, 'get', { params: { params }, onBehalf: uid });
    }

    async createLocation(location: AW.Location, uid: string): Promise<any> {
        return this.httpService.dynamicHttp(`api_location`, 'post', { body: { location }, onBehalf: uid });
    }

    async updateLocation(uid: string, location: AW.Location): Promise<any> {
        return this.httpService.dynamicHttp(`api_location`, 'put', { body: location, onBehalf: uid });
    }

    async deleteLocation(uid: string, locationId: string): Promise<any> {
        return this.httpService.dynamicHttp(`api_location/${locationId}`, 'delete', { onBehalf: uid });
    }

    async updateBookingSettings(req: { uid: string; locationId: string; settings: AW.Location.BookingSettings; booking_fee_enabled: boolean }): Promise<any> {
        return this.httpService.dynamicHttp(`api_location/update_booking_settings`, 'put', { body: req, onBehalf: req.uid });
    }

    async getDeviceStats(uid: string, location_id: string): Promise<{ terminals: Record<string, AW.Terminal.Unit>; devices: Record<string, AW.Device.Unit>; hubs: AW.Hub.Hub[]; axels: AW.Axel.Unit[] }> {
        return this.httpService.dynamicHttp(`api_device/stats`, 'get', { params: { location: location_id }, onBehalf: uid });
    }

    // Temporary - TODO: Remove these October 1, 2026
    async getUncategorizedLocations(uid: string): Promise<AW.Location.UncategorizedLocation[]> {
        return this.httpService.dynamicHttp(`api_location/uncategorized`, 'get', { onBehalf: uid });
    }

    async bulkUpdateLocationType(uid: string, updates: AW.Location.BulkLocationTypeUpdate[]): Promise<any> {
        return this.httpService.dynamicHttp(`api_location/bulk_location_type`, 'put', { body: { updates }, onBehalf: uid });
    }

    // Notice Board Endpoints

    async getBoardLocationStats(boardLocations: AW.NoticeBoard.BoardLocation[]): Promise<AW.NoticeBoard.StatsWithLocationId[]> {
        const locationUids: string[] = boardLocations.map(booking => booking.location_id);
        return await this.httpService.dynamicHttp('api_location/get_board_location_stats', 'get', { params: { locationUids } });
    }

    // Notice board calls are made on behalf of the customer owning the location, so operators can manage subcustomer boards
    async getNotices(location_id: string, onBehalf: string): Promise<AW.NoticeBoard[]> {
        return this.httpService.dynamicHttp(`api_location/get-all-notice_board/${location_id}`, 'get', { onBehalf });
    }
    async createNotice(notice: AW.NoticeBoard.CreateParams, location_id: string, onBehalf: string): Promise<AW.NoticeBoard> {
        return await this.httpService.dynamicHttp(`api_location/create-notice_board/${location_id}`, 'post', { body: notice, onBehalf });
    }
    async updateNotice(location_id: string, notice_board_item_id: string, notice: AW.NoticeBoard.UpdateParams, onBehalf: string): Promise<void> {
        return this.httpService.dynamicHttp(`api_location/update-notice_board/${location_id}/${notice_board_item_id}`, 'put', { body: notice, onBehalf });
    }
    async archiveNotice(location_id: string, notice_board_item_id: string, onBehalf: string): Promise<void> {
        return this.httpService.dynamicHttp(`api_location/archive-notice_board/${location_id}/${notice_board_item_id}`, 'delete', { onBehalf });
    }
    public debounceUpdateNoticeDisplayOrder(location_id: string, ordered_ids: AW.NoticeBoard.UpdateDisplayOrderParams, onBehalf: string): void {
        if (this.orderUpdateTimeout) {
            clearTimeout(this.orderUpdateTimeout);
        }

        this.orderUpdateTimeout = setTimeout(() => {
            this.updateNoticeDisplayOrder(location_id, ordered_ids, onBehalf);
            this.orderUpdateTimeout = null; // Clear the timer ID
        }, 300); // 1-second delay
    }

    private async updateNoticeDisplayOrder(location_id: string, ordered_ids: AW.NoticeBoard.UpdateDisplayOrderParams, onBehalf: string): Promise<void> {
        return this.httpService.dynamicHttp(`api_location/update-display-order-notice_board/${location_id}`, 'put', { body: ordered_ids, onBehalf });
    }

    public hasBankAccountsAvailable(
        payoutAndBankAccounts: { payoutSettings: AW.Account.PayoutSettings; bankAccounts?: (AW.BankAccount & { docId: string })[] },
        operatorPayoutAndBankAccounts: { payoutSettings: AW.Account.PayoutSettings; bankAccounts?: (AW.BankAccount & { docId: string })[] }
    ): boolean {
        // Run through all bank accounts, subcustomer or just the customer itself, if any is there set true, also only if there is auto
        const isManual = payoutAndBankAccounts.payoutSettings ? !payoutAndBankAccounts.payoutSettings.auto : true;
        const isOperatorManual = operatorPayoutAndBankAccounts.payoutSettings ? !operatorPayoutAndBankAccounts.payoutSettings.auto : true;

        if (isManual && payoutAndBankAccounts.bankAccounts && payoutAndBankAccounts.bankAccounts.length > 0) {
            return true;
        }

        if (isOperatorManual && operatorPayoutAndBankAccounts.bankAccounts && operatorPayoutAndBankAccounts.bankAccounts.length > 0) {
            return true;
        }

        return false;
    }
}
